import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";

/**
 * Safe lesson retake. Never touches wallet, XP, badges, or module completion.
 * Only clears the in-progress lesson_progress row (via server RPC) and records
 * the retake as its own attempt.
 */
export function useLessonRetake() {
  const [pending, setPending] = useState(false);

  const startRetake = useCallback(async (lessonId: string) => {
    setPending(true);
    try {
      const { data, error } = await supabase.rpc("start_lesson_retake", {
        p_lesson_id: lessonId,
      });
      if (error) throw error;
      return data as { attempt_number: number };
    } catch (err: unknown) {
      toast.error("Couldn't start retake", { description: getErrorMessage(err) ?? "Please try again." });
      throw err;
    } finally {
      setPending(false);
    }
  }, []);

  const finalizeRetake = useCallback(
    async (lessonId: string, score: number, passed: boolean) => {
      try {
        await supabase.rpc("finalize_lesson_retake", {
          p_lesson_id: lessonId,
          p_score: score,
          p_passed: passed,
        });
      } catch (err) {
        // Non-fatal: history log only.
        console.warn("[lesson-retake] finalize failed", err);
      }
    },
    []
  );

  return { startRetake, finalizeRetake, pending };
}